// frontend/src/components/FinanceReport/Accounting/our_allocations/profit_allocations/ProfitDocumentsModal.jsx

import { useEffect, useMemo, useRef, useState } from "react";
import {
  deleteProfitAllocationDocument,
  uploadProfitAllocationDocument,
} from "./requests";
import { authFetch } from "../../../../api/api";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp";

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("ru-RU");
}

async function downloadFileAsBlob(url, filename) {
  const response = await authFetch(url);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename || "document";
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }
}

export default function ProfitDocumentsModal({ open, item, onClose, onChanged }) {
  const documents = useMemo(() => item?.documents || [], [item]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    setError("");
    setUploadError("");
  }, [open, item?.id]);

  if (!open || !item) return null;

  const handleDownload = async (doc) => {
    setError("");
    setDownloadingId(doc.id);
    const url = doc.file_url || doc.file;
    const filename = doc.original_name || `document-${doc.id}`;
    try {
      await downloadFileAsBlob(url, filename);
    } catch {
      const win = window.open(url, "_blank");
      if (!win) setError("Не удалось скачать файл. Разрешите всплывающие окна.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (doc) => {
    const name = doc.original_name || `#${doc.id}`;
    if (!window.confirm(`Удалить документ "${name}"?`)) return;
    setError("");
    setDeletingId(doc.id);
    try {
      await deleteProfitAllocationDocument(doc.id);
      if (onChanged) await onChanged();
    } catch (err) {
      setError(err.message || "Не удалось удалить документ");
    } finally {
      setDeletingId(null);
    }
  };

  const handleUpload = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;

    setUploadError("");
    setUploading(true);
    const errs = [];
    try {
      for (const file of picked) {
        try {
          await uploadProfitAllocationDocument(item.id, file);
        } catch (err) {
          errs.push(`«${file.name}»: ${err.message}`);
        }
      }
      if (onChanged) await onChanged();
      if (errs.length) setUploadError(errs.join("\n"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="my-8 w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Документы прибыли #{item.id}
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              {item.account_name || `Account #${item.account}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-slate-500 hover:bg-slate-100"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 whitespace-pre-wrap rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              {uploading ? "Загрузка…" : "⬆ Загрузить документы"}
            </button>
            <span className="text-xs text-slate-500">
              PDF, JPG, JPEG, PNG, WEBP · можно выбрать несколько
            </span>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept={ACCEPT}
              onChange={handleUpload}
              className="hidden"
            />
          </div>
          {uploadError && (
            <div className="mt-3 whitespace-pre-wrap rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
              {uploadError}
            </div>
          )}
        </div>

        {documents.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
            <div className="text-3xl">📭</div>
            <p className="mt-2 text-sm text-slate-600">
              К этой прибыли нет документов.
            </p>
            <p className="mt-1 text-xs text-slate-400">
              Загрузите файлы кнопкой выше.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3"
              >
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-slate-100 text-lg">
                  📄
                </div>
                <div className="min-w-0 flex-1">
                  <a
                    href={doc.file_url || doc.file}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate text-sm font-medium text-blue-700 hover:underline"
                  >
                    {doc.original_name || `Документ #${doc.id}`}
                  </a>
                  <p className="text-xs text-slate-500">{formatDate(doc.uploaded_at)}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    onClick={() => handleDownload(doc)}
                    disabled={downloadingId === doc.id}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {downloadingId === doc.id ? "Скачивание…" : "⬇ Скачать"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(doc)}
                    disabled={deletingId === doc.id}
                    className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    {deletingId === doc.id ? "Удаление…" : "🗑"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}